<template>
  <v-col cols="12" class="pedido-produtos pa-0">
    <div class="pedido-produtos__workspace">
      <section class="pedido-produtos__panel pedido-produtos__catalog">
        <div class="pedido-produtos__heading">
          <div>
            <div class="pedido-produtos__eyebrow">1. Escolha os produtos</div>
            <h3 class="pedido-produtos__title">{{ tituloCatalogo }}</h3>
          </div>
          <v-chip small color="primary" text-color="white">
            {{ produtosFiltrados.length }} disponíveis
          </v-chip>
        </div>

        <v-text-field
          v-model="search"
          label="Buscar por nome, tamanho ou código de barras"
          prepend-inner-icon="mdi-magnify"
          clearable
          hide-details
          class="
            input-style
            font-size-input
            text-light-input
            placeholder-light
            input-icon
            mb-4
          "
          dense
          flat
          filled
          solo
        />

        <div v-if="produtosFiltrados.length" class="pedido-produtos__list">
          <button
            v-for="produto in produtosFiltrados"
            :key="produto.id"
            type="button"
            class="pedido-produtos__product"
            :disabled="readonly"
            @click="openQuantity(produto)"
          >
            <span class="pedido-produtos__product-icon">
              <v-icon color="primary" size="22">{{ productIcon }}</v-icon>
            </span>
            <span class="pedido-produtos__copy">
              <strong>{{ produto.descricao }}</strong>
              <small v-if="productMeta(produto)">
                {{ productMeta(produto) }}
              </small>
            </span>
            <v-icon color="primary">mdi-plus-circle</v-icon>
          </button>
        </div>

        <div v-else class="pedido-produtos__empty">
          <v-icon size="38" color="#adb5bd">mdi-package-variant</v-icon>
          <strong>Nenhum produto encontrado</strong>
          <span>Tente buscar por outro nome, tamanho ou código.</span>
        </div>
      </section>

      <section class="pedido-produtos__panel pedido-produtos__summary">
        <div class="pedido-produtos__heading">
          <div>
            <div class="pedido-produtos__eyebrow">
              2. Confira as quantidades
            </div>
            <h3 class="pedido-produtos__title">Resumo do pedido</h3>
          </div>
          <v-chip small outlined color="primary">
            {{ items.length }} {{ items.length === 1 ? "item" : "itens" }}
          </v-chip>
        </div>

        <div v-if="items.length" class="pedido-produtos__selected-list">
          <article
            v-for="item in items"
            :key="itemKey(item)"
            class="pedido-produtos__selected"
          >
            <div class="pedido-produtos__copy">
              <strong>{{ productFromItem(item).descricao }}</strong>
              <small v-if="itemMeta(item)">{{ itemMeta(item) }}</small>
              <small v-if="mostrarQuantidadeDevolvida">
                Quantidade devolvida: {{ item.quantidadeDevolvida || 0 }}
              </small>
            </div>
            <div class="pedido-produtos__quantity" aria-label="Quantidade">
              <v-btn
                icon
                small
                color="primary"
                :disabled="readonly || item.quantidadeSolicitada <= 1"
                @click="changeQuantity(item, -1)"
              >
                <v-icon small>mdi-minus</v-icon>
              </v-btn>
              <input
                :value="item.quantidadeSolicitada"
                type="number"
                min="1"
                :readonly="readonly"
                aria-label="Quantidade solicitada"
                @change="setQuantity(item, $event.target.value)"
              />
              <v-btn
                icon
                small
                color="primary"
                :disabled="readonly"
                @click="changeQuantity(item, 1)"
              >
                <v-icon small>mdi-plus</v-icon>
              </v-btn>
            </div>
            <v-btn
              icon
              small
              color="error"
              :disabled="readonly"
              :aria-label="`Remover ${productFromItem(item).descricao}`"
              @click="removeItem(item)"
            >
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
          </article>
        </div>

        <div
          v-else
          class="pedido-produtos__empty pedido-produtos__empty--order"
        >
          <v-icon size="42" color="#adb5bd">mdi-basket-outline</v-icon>
          <strong>Seu pedido ainda está vazio</strong>
          <span>Selecione um produto ao lado para começar.</span>
        </div>

        <v-alert
          v-if="items.length"
          text
          dense
          color="success"
          icon="mdi-check-circle-outline"
          class="mt-4 mb-0"
        >
          <div class="pedido-produtos__totals">
            <span>
              {{ totalUnits }}
              {{
                totalUnits === 1
                  ? "unidade selecionada"
                  : "unidades selecionadas"
              }}
            </span>
            <strong v-if="mostrarValorUnitario">
              Subtotal: {{ formatMoney(totalValue) }}
            </strong>
          </div>
        </v-alert>
      </section>
    </div>

    <v-dialog v-model="quantityDialog" max-width="420" persistent>
      <v-card class="pedido-produtos__dialog">
        <v-card-title class="pb-2">Adicionar ao pedido</v-card-title>
        <v-card-text>
          <div class="pedido-produtos__dialog-product">
            {{ selectedProduct && selectedProduct.descricao }}
          </div>
          <div
            v-if="selectedProductMeta"
            class="text-body-2 text--secondary mt-1"
          >
            {{ selectedProductMeta }}
          </div>
          <v-text-field
            ref="quantityInput"
            v-model.number="selectedQuantity"
            type="number"
            min="1"
            label="Quantidade"
            prepend-inner-icon="mdi-counter"
            autofocus
            class="input-style mt-4"
            dense
            flat
            filled
            solo
            @keydown.enter.prevent="confirmQuantity"
          />
          <v-text-field
            v-if="mostrarValorUnitario"
            v-model.number="selectedUnitValue"
            type="number"
            min="0"
            step="0.01"
            prefix="R$"
            label="Valor unitário"
            prepend-inner-icon="mdi-currency-brl"
            class="input-style"
            dense
            flat
            filled
            solo
            @keydown.enter.prevent="confirmQuantity"
          />
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer />
          <v-btn
            height="39"
            class="
              ma-1
              font-weight-bold
              text-capitalize
              btn-ls btn-secondary
              bg-gradient-light
              px-6
            "
            @click="closeQuantity"
          >
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-btn
            class="
              ma-1
              font-weight-bold
              text-none
              btn-primary
              bg-gradient-primary
              px-6
              ms-3
            "
            height="39"
            :disabled="!validSelection"
            @click="confirmQuantity"
          >
            <v-icon left small>mdi-plus</v-icon>
            Adicionar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
export default {
  name: "PedidoProdutosSeletor",
  props: {
    value: { type: Array, default: () => [] },
    produtos: { type: Array, default: () => [] },
    tituloCatalogo: { type: String, default: "Catálogo de produtos" },
    productIcon: { type: String, default: "mdi-package-variant-closed" },
    mostrarTamanho: { type: Boolean, default: false },
    mostrarCodigoBarras: { type: Boolean, default: false },
    mostrarEstoque: { type: Boolean, default: false },
    mostrarValorUnitario: { type: Boolean, default: false },
    mostrarQuantidadeDevolvida: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      search: "",
      quantityDialog: false,
      selectedProduct: null,
      selectedQuantity: 1,
      selectedUnitValue: 0,
    };
  },
  computed: {
    items() {
      return Array.isArray(this.value) ? this.value : [];
    },
    produtosFiltrados() {
      const term = this.normalize(this.search);
      if (!term) return this.produtos;
      return this.produtos.filter((produto) =>
        [produto.descricao, produto.tamanho, produto.cbarras]
          .filter(Boolean)
          .some((field) => this.normalize(field).includes(term))
      );
    },
    totalUnits() {
      return this.items.reduce(
        (total, item) => total + Number(item.quantidadeSolicitada || 0),
        0
      );
    },
    totalValue() {
      return this.items.reduce(
        (total, item) =>
          total +
          Number(item.quantidadeSolicitada || 0) *
            Number(item.valorUnitario || 0),
        0
      );
    },
    validSelection() {
      const validQuantity =
        Number.isInteger(Number(this.selectedQuantity)) &&
        Number(this.selectedQuantity) > 0;
      const validValue =
        !this.mostrarValorUnitario || Number(this.selectedUnitValue) >= 0;
      return validQuantity && validValue;
    },
    selectedProductMeta() {
      return this.selectedProduct ? this.productMeta(this.selectedProduct) : "";
    },
  },
  methods: {
    normalize(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
    },
    productFromItem(item) {
      return item?.produto || {};
    },
    itemKey(item) {
      return this.productFromItem(item).id || item.id;
    },
    productMeta(produto) {
      const metadata = [];
      if (this.mostrarTamanho && produto.tamanho) {
        metadata.push(`Tamanho: ${produto.tamanho}`);
      }
      if (this.mostrarCodigoBarras && produto.cbarras) {
        metadata.push(produto.cbarras);
      }
      const stock = this.productStock(produto);
      if (this.mostrarEstoque && stock !== null) {
        metadata.push(`Disponível: ${stock}`);
      }
      return metadata.join(" · ");
    },
    productStock(produto) {
      if (produto.estoque !== undefined && produto.estoque !== null) {
        return produto.estoque;
      }
      const firstStock = Array.isArray(produto.estoques)
        ? produto.estoques[0]
        : null;
      return firstStock?.estoque ?? null;
    },
    itemMeta(item) {
      const metadata = [];
      const productMetadata = this.productMeta(this.productFromItem(item));
      if (productMetadata) metadata.push(productMetadata);
      if (this.mostrarValorUnitario) {
        metadata.push(
          `Valor unitário: ${this.formatMoney(item.valorUnitario)}`
        );
      }
      return metadata.join(" · ");
    },
    formatMoney(value) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(value || 0));
    },
    openQuantity(product) {
      if (this.readonly) return;
      this.selectedProduct = product;
      const current = this.items.find(
        (item) => this.productFromItem(item).id === product.id
      );
      this.selectedQuantity = current
        ? Number(current.quantidadeSolicitada || 1)
        : 1;
      this.selectedUnitValue = current ? Number(current.valorUnitario || 0) : 0;
      this.quantityDialog = true;
      this.$nextTick(() => this.$refs.quantityInput?.focus?.());
    },
    closeQuantity() {
      this.quantityDialog = false;
      this.selectedProduct = null;
      this.selectedQuantity = 1;
      this.selectedUnitValue = 0;
    },
    confirmQuantity() {
      if (!this.selectedProduct || !this.validSelection) return;
      const current = this.items.find(
        (item) => this.productFromItem(item).id === this.selectedProduct.id
      );
      const nextItem = {
        ...(current || {}),
        produto: this.selectedProduct,
        quantidadeSolicitada: Number(this.selectedQuantity),
      };
      if (this.mostrarValorUnitario) {
        nextItem.valorUnitario = Number(this.selectedUnitValue || 0);
      }
      const items = current
        ? this.items.map((item) =>
            this.productFromItem(item).id === this.selectedProduct.id
              ? nextItem
              : item
          )
        : [...this.items, nextItem];
      this.$emit("input", items);
      this.closeQuantity();
    },
    setQuantity(item, value) {
      if (this.readonly) return;
      const quantity = Math.max(1, parseInt(value, 10) || 1);
      const itemId = this.itemKey(item);
      this.$emit(
        "input",
        this.items.map((current) =>
          this.itemKey(current) === itemId
            ? { ...current, quantidadeSolicitada: quantity }
            : current
        )
      );
    },
    changeQuantity(item, delta) {
      this.setQuantity(item, Number(item.quantidadeSolicitada || 1) + delta);
    },
    removeItem(item) {
      const itemId = this.itemKey(item);
      this.$emit(
        "input",
        this.items.filter((current) => this.itemKey(current) !== itemId)
      );
    },
  },
};
</script>

<style scoped>
.pedido-produtos__workspace {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(320px, 0.8fr);
  gap: 20px;
  margin-top: 12px;
}

.pedido-produtos__panel {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 16px;
  min-width: 0;
  padding: 22px;
}

.pedido-produtos__summary {
  background: linear-gradient(180deg, #fafbff 0%, #fff 100%);
}

.pedido-produtos__heading {
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 18px;
}

.pedido-produtos__eyebrow {
  color: #5e72e4;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.pedido-produtos__title {
  color: #344767;
  font-size: 1.18rem;
  margin: 4px 0 0;
}

.pedido-produtos__list,
.pedido-produtos__selected-list {
  display: flex;
  flex-direction: column;
  gap: 9px;
  max-height: 390px;
  overflow-y: auto;
  padding-right: 4px;
}

.pedido-produtos__product {
  align-items: center;
  background: #fff;
  border: 1px solid #edf0f7;
  border-radius: 12px;
  color: #344767;
  cursor: pointer;
  display: flex;
  gap: 12px;
  padding: 12px;
  text-align: left;
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;
  width: 100%;
}

.pedido-produtos__product:hover:not(:disabled),
.pedido-produtos__product:focus-visible:not(:disabled) {
  border-color: #5e72e4;
  box-shadow: 0 7px 18px rgba(94, 114, 228, 0.12);
  outline: none;
  transform: translateY(-1px);
}

.pedido-produtos__product:disabled {
  cursor: default;
  opacity: 0.72;
}

.pedido-produtos__product-icon {
  align-items: center;
  background: #f1f3ff;
  border-radius: 10px;
  display: flex;
  height: 42px;
  justify-content: center;
  min-width: 42px;
}

.pedido-produtos__copy {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.pedido-produtos__copy strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pedido-produtos__copy small {
  color: #7b809a;
  margin-top: 3px;
}

.pedido-produtos__selected {
  align-items: center;
  background: #fff;
  border: 1px solid #edf0f7;
  border-radius: 12px;
  display: flex;
  gap: 8px;
  padding: 11px 9px 11px 13px;
}

.pedido-produtos__quantity {
  align-items: center;
  background: #f6f7fb;
  border-radius: 9px;
  display: flex;
}

.pedido-produtos__quantity input {
  background: transparent;
  border: 0;
  color: #344767;
  font-size: 0.9rem;
  font-weight: 700;
  outline: 0;
  text-align: center;
  width: 42px;
}

.pedido-produtos__quantity input::-webkit-inner-spin-button,
.pedido-produtos__quantity input::-webkit-outer-spin-button {
  appearance: none;
  margin: 0;
}

.pedido-produtos__empty {
  align-items: center;
  color: #7b809a;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  min-height: 210px;
  text-align: center;
}

.pedido-produtos__empty--order {
  min-height: 250px;
}

.pedido-produtos__empty strong {
  color: #344767;
}

.pedido-produtos__dialog {
  border-radius: 16px;
}

.pedido-produtos__dialog-product {
  color: #344767;
  font-weight: 700;
}

.pedido-produtos__totals {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

@media (max-width: 960px) {
  .pedido-produtos__workspace {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .pedido-produtos__panel {
    padding: 16px;
  }

  .pedido-produtos__selected {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .pedido-produtos__copy {
    flex-basis: calc(100% - 42px);
  }

  .pedido-produtos__dialog .v-card__actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .pedido-produtos__dialog .v-card__actions .v-btn {
    margin: 4px 0 !important;
    width: 100%;
  }
}
</style>
